import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AppWindow, Fingerprint, Tag, ShieldAlert, Video, Globe2 } from "lucide-react";

const ADVANTAGES = [
  {
    icon: AppWindow,
    title: "Никакого браузера — только ПО",
    desc: "Полноценное приложение для компьютера и телефона — быстрее и стабильнее любого браузера.",
    bubble: "Это не сайт в браузере, а настоящее приложение",
  },
  {
    icon: Fingerprint,
    title: "Один сертификат — все устройства",
    desc: "Используйте сертификат на ноутбуке, в командировке и в мобильном приложении — подписывайте откуда угодно.",
    bubble: "Один сертификат работает везде, даже в дороге",
  },
  {
    icon: Tag,
    title: "1,99 ₽ за документ",
    desc: "Честная и реальная цена, а не заниженная приманка — ровно столько стоит обработка одного документа.",
    bubble: "1,99 ₽ — это реальная цена, без скрытых условий",
  },
  {
    icon: ShieldAlert,
    title: "Проверка контрагента",
    desc: "Автоматическая сверка партнёра по базам — узнаете о негативе и рисках ещё до подписания.",
    bubble: "Проверим контрагента по базам за вас",
  },
  {
    icon: Video,
    title: "Звонок прямо из переписки",
    desc: "Обсудите спорный документ с партнёром по видео или телефону, не покидая кабинет.",
    bubble: "Можно созвониться с партнёром прямо здесь",
  },
  {
    icon: Globe2,
    title: "Роуминг со всеми ЭДО",
    desc: "Обмен настроен и работает с другими операторами ЭДО — неважно, каким сервисом пользуется партнёр.",
    bubble: "Роуминг подключён — обмениваемся с любым оператором",
  },
];

const CYCLE_MS = 4200;

/** Standing "narrator" character — gestures toward the speech bubble above its head. */
function Narrator({ talking }) {
  return (
    <motion.g animate={{ y: [0, -5, 0] }} transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}>
      {/* legs */}
      <rect x={82} y={214} width={16} height={46} rx={7} fill="#1E293B" />
      <rect x={102} y={214} width={16} height={46} rx={7} fill="#1E293B" />
      <ellipse cx={90} cy={262} rx={13} ry={5} fill="#0F172A" />
      <ellipse cx={110} cy={262} rx={13} ry={5} fill="#0F172A" />
      {/* resting arm */}
      <path d="M 78 150 Q 62 170 66 196" stroke="#6366F1" strokeWidth={14} strokeLinecap="round" fill="none" />
      <circle cx={67} cy={199} r={8} fill="#F3C19C" />
      {/* torso */}
      <path d="M 68 214 Q 64 156 100 150 Q 136 156 132 214 Z" fill="#6366F1" />
      {/* gesturing arm, points up toward the speech bubble */}
      <motion.path
        d="M 122 152 Q 148 136 150 104"
        stroke="#6366F1"
        strokeWidth={14}
        strokeLinecap="round"
        fill="none"
        animate={talking ? { rotate: [0, -8, 0] } : { rotate: 0 }}
        transition={{ duration: 0.9, repeat: talking ? Infinity : 0, ease: "easeInOut" }}
        style={{ transformOrigin: "122px 152px" }}
      />
      <circle cx={150} cy={101} r={9} fill="#F3C19C" />
      {/* neck + head */}
      <rect x={92} y={120} width={16} height={14} fill="#F3C19C" />
      <circle cx={100} cy={106} r={24} fill="#F3C19C" />
      {/* hair */}
      <path d="M 76 102 Q 74 72 100 70 Q 126 72 124 102 Q 122 84 100 84 Q 78 84 76 102 Z" fill="#2D2420" />
      {/* smile + eyes */}
      <path d="M 92 112 Q 100 119 108 112" stroke="#8B5E3C" strokeWidth={2} fill="none" strokeLinecap="round" />
      <circle cx={92} cy={102} r={2} fill="#2D2420" />
      <circle cx={108} cy={102} r={2} fill="#2D2420" />
    </motion.g>
  );
}

export function LandingAdvantages() {
  const [active, setActive] = useState(0);
  const [talking, setTalking] = useState(true);
  const timerRef = useRef(null);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActive((a) => (a + 1) % ADVANTAGES.length), CYCLE_MS);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    setTalking(true);
    const t = setTimeout(() => setTalking(false), 1400);
    return () => clearTimeout(t);
  }, [active]);

  const goTo = (i) => {
    setActive(i);
    startTimer();
  };

  const current = ADVANTAGES[active];

  return (
    <section id="advantages" className="py-20 md:py-28 bg-slate-50" data-testid="landing-advantages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#2563EB] mb-3">Преимущества</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Шесть причин выбрать КриптоДок
          </h2>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8 lg:gap-12 items-start">
          <div className="relative mx-auto lg:mx-0 w-[220px] sm:w-[260px]" data-testid="landing-advantage-narrator">
            <svg viewBox="0 0 200 270" className="w-full h-auto">
              <Narrator talking={talking} />
            </svg>
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-[200px] sm:w-[230px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 8, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.94 }}
                  transition={{ duration: 0.3 }}
                  className="relative bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.15)] text-xs sm:text-sm font-medium text-slate-700 text-center"
                  data-testid="landing-advantage-bubble"
                >
                  {current.bubble}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {ADVANTAGES.map((a, i) => (
              <button
                key={a.title}
                onClick={() => goTo(i)}
                data-testid={`landing-advantage-card-${i}`}
                className={`text-left rounded-2xl border p-5 transition-all duration-300 ${
                  i === active
                    ? "border-[#6366F1]/40 bg-white shadow-[0_12px_30px_-10px_rgba(99,102,241,0.35)] scale-[1.02]"
                    : "border-slate-200 bg-white/60 hover:border-slate-300"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors ${
                    i === active ? "bg-[#6366F1]/10 text-[#6366F1]" : "bg-slate-100 text-slate-400"
                  }`}
                >
                  <a.icon className="w-5 h-5" />
                </div>
                <div className="text-sm sm:text-base font-semibold text-slate-900 mb-1">{a.title}</div>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{a.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
